import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Footer from '../components/organisms/Footer';
import Button from '../components/atoms/Button';
import Icon from '../components/atoms/Icon';
import SessionManager from '../utils/sessionManager';
import BookDemoModal from '../components/organisms/BookDemoModal';
import InPageVoiceDemo from '../components/InPageVoiceDemo';

// Animated Counter Components
const AnimatedCounter = ({ end, duration = 2000, suffix = '', prefix = '' }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const counterRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (counterRef.current) {
      observer.observe(counterRef.current);
    }

    return () => {
      if (counterRef.current) {
        observer.unobserve(counterRef.current);
      }
    };
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    let startTime;
    let animationFrame;

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = (currentTime - startTime) / duration;

      if (progress < 1) {
        setCount(Math.floor(end * progress));
        animationFrame = requestAnimationFrame(animate);
      } else {
        setCount(end);
      }
    };

    animationFrame = requestAnimationFrame(animate);

    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [isVisible, end, duration]);

  return (
    <span ref={counterRef}>
      {prefix}{count.toLocaleString()}{suffix}
    </span>
  );
};

const RecruiterLandingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentSession, setCurrentSession] = useState(null);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [showBookDemoModal, setShowBookDemoModal] = useState(false);

  useEffect(() => {
    document.title = 'Aptirix for Recruiters - AI-Powered Hiring';
    
    const session = SessionManager.getCurrentSession();
    setCurrentSession(session);

    // Scroll to top if coming from jobs page
    if (location.state?.scrollToForm) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    // Show Book Demo popup once per session
    const hasSeenDemoPopup = sessionStorage.getItem('hasSeenDemoPopup');
    if (!hasSeenDemoPopup) {
      // Show popup after 2 seconds
      const timer = setTimeout(() => {
        setShowBookDemoModal(true);
        sessionStorage.setItem('hasSeenDemoPopup', 'true');
      }, 2000);
      
      return () => clearTimeout(timer);
    }

    const handleClickOutside = (event) => {
      if (!event.target.closest('.profile-dropdown-container')) {
        setProfileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [location]);

  const handleBookDemo = () => {
    setShowBookDemoModal(true);
  };

  const handleContactUs = () => {
    window.location.href = 'mailto:contact@aptirix.com?subject=Contact Inquiry';
  };

  const handleSmoothScroll = (e, targetId) => {
    e.preventDefault();
    const element = document.getElementById(targetId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const isHR = currentSession?.role === 'hr';

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center">
            <span className="font-bold text-2xl sm:text-3xl text-gray-900">
              Aptirix
            </span>
            <span className="ml-2 text-sm text-gray-600 hidden sm:inline">for Recruiters</span>
          </div>

          {/* Navigation Links & Auth */}
          <div className="flex items-center gap-6">
            {/* Navigation Links */}
            <div className="hidden md:flex items-center gap-6">
              <a 
                href="#features" 
                onClick={(e) => handleSmoothScroll(e, 'features')}
                className="text-gray-700 hover:text-[#2b6cee] font-medium transition-colors cursor-pointer"
              >
                Features
              </a>
              <a 
                href="#how-it-works" 
                onClick={(e) => handleSmoothScroll(e, 'how-it-works')}
                className="text-gray-700 hover:text-[#2b6cee] font-medium transition-colors cursor-pointer"
              >
                How It Works
              </a>
              <button
                onClick={() => navigate('/pricing')}
                className="text-gray-700 hover:text-[#2b6cee] font-medium transition-colors"
              >
                Pricing
              </button>
            </div>

            {/* Auth Section */}
            {currentSession && currentSession.role === 'hr' ? (
              <div className="relative profile-dropdown-container">
                <button
                  onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                  className="flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 sm:px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-[#2b6cee] to-[#1a4fb8] flex items-center justify-center">
                    <span className="text-white text-sm font-semibold">
                      {(currentSession.email?.charAt(0) || 'U').toUpperCase()}
                    </span>
                  </div>
                  <span className="max-w-[120px] truncate hidden sm:inline">
                    {currentSession.email?.split('@')[0] || 'User'}
                  </span>
                  <Icon name="expand_more" size={20} />
                </button>
                
                {profileMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 rounded-lg border border-gray-200 bg-white p-2 shadow-xl">
                    <div className="px-3 py-2 border-b border-gray-100">
                      <p className="text-xs text-gray-500 truncate">{currentSession.email}</p>
                    </div>
                    <button
                      onClick={() => navigate('/hr/dashboard')}
                      className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-gray-700 hover:bg-gray-50 mt-1"
                    >
                      <Icon name="dashboard" size={16} />
                      Dashboard
                    </button>
                    <button
                      onClick={() => {
                        SessionManager.clearAllSessions();
                        setCurrentSession(null);
                        setProfileMenuOpen(false);
                        navigate('/');
                        window.location.reload();
                      }}
                      className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-red-600 hover:bg-red-50 mt-1"
                    >
                      <Icon name="logout" size={16} />
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <button
                  onClick={() => navigate('/hr_login')}
                  className="text-gray-700 hover:text-[#2b6cee] font-medium transition-colors"
                >
                  Already a member? Sign In
                </button>
              </div>
            )}
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] text-white overflow-hidden min-h-[700px]">
        {/* Prismatic Spectrum Background */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Large center prismatic orb */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/4 w-[900px] h-[900px] rounded-full bg-gradient-to-br from-cyan-400/30 via-blue-500/35 to-indigo-600/30 blur-3xl"></div>
          
          {/* Top left - Purple to Pink spectrum */}
          <div className="absolute left-0 top-0 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-purple-500/35 via-violet-500/30 to-fuchsia-500/25 blur-3xl"></div>
          
          {/* Top right - Cyan to Blue spectrum */}
          <div className="absolute right-0 top-10 w-[450px] h-[450px] rounded-full bg-gradient-to-bl from-cyan-400/30 via-sky-500/25 to-blue-600/30 blur-3xl"></div>
          
          {/* Bottom left - Blue to Teal spectrum */}
          <div className="absolute left-10 bottom-0 w-[550px] h-[550px] rounded-full bg-gradient-to-tr from-blue-600/30 via-cyan-500/25 to-teal-400/20 blur-3xl"></div>
          
          {/* Bottom right - Pink to Orange spectrum */}
          <div className="absolute right-0 bottom-0 w-[480px] h-[480px] rounded-full bg-gradient-to-tl from-pink-500/25 via-rose-500/20 to-orange-400/15 blur-3xl"></div>
          
          {/* Mid accent orbs */}
          <div className="absolute left-1/3 top-1/3 w-[300px] h-[300px] rounded-full bg-gradient-to-br from-indigo-400/25 via-purple-500/20 to-pink-500/15 blur-2xl"></div>
          <div className="absolute right-1/3 top-2/3 w-[280px] h-[280px] rounded-full bg-gradient-to-tl from-emerald-400/20 via-teal-500/15 to-cyan-500/20 blur-2xl"></div>
          
          {/* Small accent orbs for depth */}
          <div className="absolute left-1/4 bottom-1/4 w-[200px] h-[200px] rounded-full bg-gradient-to-r from-violet-400/20 to-fuchsia-400/15 blur-xl"></div>
          <div className="absolute right-1/4 top-1/4 w-[220px] h-[220px] rounded-full bg-gradient-to-l from-sky-400/20 to-blue-500/15 blur-xl"></div>
          
          {/* Subtle white highlights */}
          <div className="absolute left-1/2 top-1/3 w-[180px] h-[180px] rounded-full bg-white/8 blur-2xl"></div>
          <div className="absolute right-1/5 bottom-1/3 w-[150px] h-[150px] rounded-full bg-white/6 blur-xl"></div>
        </div>
        
        {/* Subtle grid overlay */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxkZWZzPjxwYXR0ZXJuIGlkPSJncmlkIiB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHBhdHRlcm5Vbml0cz0idXNlclNwYWNlT25Vc2UiPjxwYXRoIGQ9Ik0gNDAgMCBMIDAgMCAwIDQwIiBmaWxsPSJub25lIiBzdHJva2U9InJnYmEoMjU1LDI1NSwyNTUsMC4wNSkiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-40"></div>
        
        {/* Text Animation Styles */}
        <style>{`
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideInLeft {
            from {
              opacity: 0;
              transform: translateX(-50px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
          @keyframes slideInRight {
            from {
              opacity: 0;
              transform: translateX(50px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
          @keyframes scaleIn {
            from {
              opacity: 0;
              transform: scale(0.8);
            }
            to {
              opacity: 1;
              transform: scale(1);
            }
          }
          .animate-fadeInUp {
            animation: fadeInUp 0.8s ease-out forwards;
          }
          .animate-fadeIn {
            animation: fadeIn 1s ease-out forwards;
          }
          .animate-slideInLeft {
            animation: slideInLeft 0.8s ease-out forwards;
          }
          .animate-slideInRight {
            animation: slideInRight 0.8s ease-out forwards;
          }
          .animate-scaleIn {
            animation: scaleIn 0.6s ease-out forwards;
          }
          .animation-delay-200 { animation-delay: 0.2s; opacity: 0; }
          .animation-delay-400 { animation-delay: 0.4s; opacity: 0; }
          .animation-delay-600 { animation-delay: 0.6s; opacity: 0; }
          .animation-delay-800 { animation-delay: 0.8s; opacity: 0; }
          .animation-delay-1000 { animation-delay: 1s; opacity: 0; }
        `}</style>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-32">
          <div className="text-center max-w-4xl mx-auto flex flex-col items-center">
            <div className="inline-block mb-6 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm font-medium border border-white/20 animate-scaleIn">
              AI-POWERED RECRUITMENT PLATFORM
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6 leading-tight">
              <span className="block animate-fadeInUp">Hire Smarter with</span>
              <span className="block animate-fadeInUp animation-delay-200">AI-Powered Interviews</span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-200 mb-8 max-w-3xl mx-auto animate-fadeIn animation-delay-400">
              Transform your hiring process with live AI interviews, real-time candidate assessment, 
              and data-driven insights. Make better hiring decisions faster.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center w-full animate-fadeInUp animation-delay-600">
              <Button 
                size="xl" 
                onClick={handleBookDemo}
                className="bg-white text-gray-900 hover:bg-gray-100 px-10 py-4 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all inline-flex items-center justify-center gap-2"
              >
                <Icon name="calendar_today" size={20} />
                Book a Demo
              </Button>
              <Button 
                size="xl" 
                variant="outline"
                onClick={() => navigate(isHR ? '/hr/dashboard' : '/hr_login')}
                className="border-2 border-white text-white hover:bg-white/10 px-10 py-4 text-lg font-semibold rounded-xl inline-flex items-center justify-center gap-2"
              >
                <Icon name={isHR ? "dashboard" : "login"} size={20} />
                {isHR ? 'Go to Dashboard' : 'Get Started'}
              </Button>
            </div>
          </div>

          {/* Feature Tags - Animated Loop */}
          <style>{`
            @keyframes slideLoop {
              0% {
                transform: translateX(0);
              }
              100% {
                transform: translateX(-50%);
              }
            }
            .animate-slide-loop {
              animation: slideLoop 20s linear infinite;
            }
            .animate-slide-loop:hover {
              animation-play-state: paused;
            }
          `}</style>
          
          <div className="mt-16 overflow-hidden relative">
            <div className="flex gap-4 animate-slide-loop">
              {[
                { icon: 'smart_toy', title: 'AI Interviews', description: 'Automated screening' },
                { icon: 'analytics', title: 'Smart Analytics', description: 'Data-driven insights' },
                { icon: 'verified', title: 'Skill Verification', description: 'Real-time assessment' },
                { icon: 'speed', title: 'Save Time', description: '80% faster hiring' },
                { icon: 'description', title: 'Document Verification', description: 'Credential checks' },
                // Duplicate for seamless loop
                { icon: 'smart_toy', title: 'AI Interviews', description: 'Automated screening' },
                { icon: 'analytics', title: 'Smart Analytics', description: 'Data-driven insights' },
                { icon: 'verified', title: 'Skill Verification', description: 'Real-time assessment' },
                { icon: 'speed', title: 'Save Time', description: '80% faster hiring' },
                { icon: 'description', title: 'Document Verification', description: 'Credential checks' },
              ].map((feature, index) => (
                <div 
                  key={index}
                  className="bg-white/10 backdrop-blur-md rounded-2xl px-6 py-4 flex items-center space-x-3 border border-white/20 hover:bg-white/15 transition-all flex-shrink-0 min-w-[280px]"
                >
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm flex-shrink-0">
                    <Icon name={feature.icon} size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">{feature.title}</h3>
                    <p className="text-sm text-gray-200">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* In-Page Voice Demo Section */}
      <InPageVoiceDemo />

      {/* Image Showcase Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left - Image */}
            <div className="order-2 lg:order-1">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://media.istockphoto.com/id/1355302972/photo/african-american-customer-woman-talking-to-support-service-employee.jpg?s=612x612&w=0&k=20&c=oqDbA9Isz4RRdTln7BfS3yoSxN1VeY5lCRfv3u6_bAI=" 
                  alt="AI-Powered Interview Platform"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Right - Content */}
            <div className="order-1 lg:order-2">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                Transform Your Hiring with AI-Powered Interviews
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Leverage cutting-edge AI technology to conduct intelligent interviews, assess candidates objectively, 
                and make data-driven hiring decisions that transform your recruitment process.
              </p>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Icon name="check_circle" size={24} className="text-green-500 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Automated Screening</h4>
                    <p className="text-gray-600">AI conducts initial interviews and screens candidates 24/7</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Icon name="check_circle" size={24} className="text-green-500 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Real-Time Assessment</h4>
                    <p className="text-gray-600">Get instant performance metrics and skill evaluations</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Icon name="check_circle" size={24} className="text-green-500 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Comprehensive Reports</h4>
                    <p className="text-gray-600">Detailed PDF reports with hiring recommendations</p>
                  </div>
                </div>
              </div>
              <Button 
                size="lg" 
                onClick={handleBookDemo}
                className="mt-8 bg-[#2b6cee] hover:bg-[#1a4fb8] text-white"
              >
                Schedule a Demo
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Aptirix for Recruitment Section */}
      <section id="features" className="py-20 bg-blue-50/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 text-center mb-16">
            Why Choose Aptirix for Recruitment?
          </h2>

          {/* Feature Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {/* Card 1 - Save Time */}
            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition-all border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Save 80% Time</h3>
              <p className="text-gray-600 text-sm mb-6">
                Reduce screening time dramatically with automated AI interviews. Focus on the best candidates while our AI handles initial assessments.
              </p>
              <button className="text-sm font-medium text-gray-900 border border-gray-900 px-4 py-2 rounded-lg hover:bg-gray-900 hover:text-white transition-colors">
                Learn More
              </button>
            </div>

            {/* Card 2 - Data-Driven */}
            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition-all border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Data-Driven Decisions</h3>
              <p className="text-gray-600 text-sm mb-6">
                Get comprehensive reports with skill assessments, behavioral analysis, and performance metrics for every candidate.
              </p>
              <button className="text-sm font-medium text-gray-900 border border-gray-900 px-4 py-2 rounded-lg hover:bg-gray-900 hover:text-white transition-colors">
                Learn More
              </button>
            </div>

            {/* Card 3 - Verified Skills */}
            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition-all border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Verified Skills</h3>
              <p className="text-gray-600 text-sm mb-6">
                Move beyond resume screening. Verify actual skills through live AI interviews with real-time proctoring and assessment.
              </p>
              <button className="text-sm font-medium text-gray-900 border border-gray-900 px-4 py-2 rounded-lg hover:bg-gray-900 hover:text-white transition-colors">
                Learn More
              </button>
            </div>

            {/* Card 4 - Better Quality */}
            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition-all border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Improve Quality</h3>
              <p className="text-gray-600 text-sm mb-6">
                Make objective hiring decisions based on actual performance, not just credentials. Reduce bad hires by up to 60%.
              </p>
              <button className="text-sm font-medium text-gray-900 border border-gray-900 px-4 py-2 rounded-lg hover:bg-gray-900 hover:text-white transition-colors">
                Learn More
              </button>
            </div>
          </div>

          {/* Statistics Section */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 pt-12 border-t border-gray-200">
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">
                <AnimatedCounter end={80} suffix="%" />
              </div>
              <div className="text-sm text-gray-600">Time Saved</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">
                <AnimatedCounter end={60} suffix="%" />
              </div>
              <div className="text-sm text-gray-600">Fewer Bad Hires</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">
                <AnimatedCounter end={10} suffix="K+" />
              </div>
              <div className="text-sm text-gray-600">Interviews Conducted</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">
                <AnimatedCounter end={95} suffix="%" />
              </div>
              <div className="text-sm text-gray-600">Client Satisfaction</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">24/7</div>
              <div className="text-sm text-gray-600">AI Availability</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">
                <AnimatedCounter end={500} suffix="+" />
              </div>
              <div className="text-sm text-gray-600">Companies Trust Us</div>
            </div>
          </div>
        </div>
      </section>

      {/* Meet Aptirix's AI Agents Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-4">
            <p className="text-sm font-semibold text-[#2b6cee] uppercase tracking-wide mb-2">
              Meet Aptirix's AI Agents
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Unleash powerful AI tools to accelerate your career and hiring success
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
            {/* AI Interview Trainer Card */}
            <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100">
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">AI Interview Trainer</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Practice interviews with real-time AI feedback and coaching
                </p>
              </div>
              
              {/* Image */}
              <div className="px-6 mb-4">
                <div className="relative rounded-xl overflow-hidden bg-gradient-to-br from-blue-50 to-indigo-50 aspect-video">
                  <img 
                    src="https://media.istockphoto.com/id/1355302972/photo/african-american-customer-woman-talking-to-support-service-employee.jpg?s=612x612&w=0&k=20&c=oqDbA9Isz4RRdTln7BfS3yoSxN1VeY5lCRfv3u6_bAI="
                    alt="AI Interview Trainer"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              {/* Features */}
              <div className="px-6 pb-4 space-y-2">
                <div className="flex items-start gap-2">
                  <Icon name="check_circle" size={18} className="text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">Personalized question generation</span>
                </div>
                <div className="flex items-start gap-2">
                  <Icon name="check_circle" size={18} className="text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">Instant AI feedback & scoring</span>
                </div>
                <div className="flex items-start gap-2">
                  <Icon name="check_circle" size={18} className="text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">Comprehensive PDF reports</span>
                </div>
              </div>

              {/* CTA Button */}
              <div className="px-6 pb-6">
                <button 
                  onClick={() => navigate('/ai-trainer')}
                  className="w-full bg-gray-900 hover:bg-gray-800 text-white font-semibold py-3 rounded-lg transition-colors"
                >
                  Try Now
                </button>
              </div>
            </div>

            {/* Smart Interview Agent Card */}
            <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100">
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Smart Interview Agent</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Automated candidate screening with AI-powered assessments
                </p>
              </div>
              
              {/* Image */}
              <div className="px-6 mb-4">
                <div className="relative rounded-xl overflow-hidden bg-gradient-to-br from-purple-50 to-pink-50 aspect-video">
                  <img 
                    src="https://img.freepik.com/free-photo/technology-integrated-everyday-life_23-2151887052.jpg?semt=ais_hybrid&w=740&q=80"
                    alt="Smart Interview Agent"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              {/* Features */}
              <div className="px-6 pb-4 space-y-2">
                <div className="flex items-start gap-2">
                  <Icon name="check_circle" size={18} className="text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">Automated candidate screening</span>
                </div>
                <div className="flex items-start gap-2">
                  <Icon name="check_circle" size={18} className="text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">Detailed performance analytics</span>
                </div>
                <div className="flex items-start gap-2">
                  <Icon name="check_circle" size={18} className="text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">Bias-free candidate evaluation</span>
                </div>
              </div>

              {/* CTA Button */}
              <div className="px-6 pb-6">
                <button 
                  onClick={() => navigate('/hr/dashboard')}
                  className="w-full bg-gray-900 hover:bg-gray-800 text-white font-semibold py-3 rounded-lg transition-colors"
                >
                  Learn More
                </button>
              </div>
            </div>

            {/* Document Verification Agent Card */}
            <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100">
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Document Verification Agent</h3>
                <p className="text-sm text-gray-600 mb-4">
                  AI-powered verification of credentials and documents
                </p>
              </div>
              
              {/* Image */}
              <div className="px-6 mb-4">
                <div className="relative rounded-xl overflow-hidden bg-gradient-to-br from-green-50 to-emerald-50 aspect-video">
                  <img 
                    src="https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=600&h=400&fit=crop"
                    alt="Document Verification"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              {/* Features */}
              <div className="px-6 pb-4 space-y-2">
                <div className="flex items-start gap-2">
                  <Icon name="check_circle" size={18} className="text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">Automated document verification</span>
                </div>
                <div className="flex items-start gap-2">
                  <Icon name="check_circle" size={18} className="text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">Credential authenticity checks</span>
                </div>
                <div className="flex items-start gap-2">
                  <Icon name="check_circle" size={18} className="text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">Fraud detection & prevention</span>
                </div>
              </div>

              {/* CTA Button */}
              <div className="px-6 pb-6">
                <button 
                  onClick={() => navigate('/hr/document-verification')}
                  className="w-full bg-gray-900 hover:bg-gray-800 text-white font-semibold py-3 rounded-lg transition-colors"
                >
                  Learn More
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Get started in three simple steps
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 relative">
            {/* Connecting lines for desktop */}
            <div className="hidden md:block absolute top-12 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-[#2b6cee]/30 to-transparent"></div>
            
            {/* Step 1 */}
            <div className="text-center relative">
              <div className="relative mb-8 inline-block">
                <div className="w-24 h-24 bg-gradient-to-br from-[#2b6cee] to-[#1a4fb8] rounded-3xl flex items-center justify-center mx-auto shadow-xl hover:scale-110 transition-transform duration-300 relative z-10">
                  <span className="text-4xl font-bold text-white">1</span>
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Post Your Job</h3>
              <p className="text-gray-600 leading-relaxed">
                Create a job posting with required skills and experience. Our AI will generate 
                custom interview questions tailored to your needs.
              </p>
            </div>

            {/* Step 2 */}
            <div className="text-center relative">
              <div className="relative mb-8 inline-block">
                <div className="w-24 h-24 bg-gradient-to-br from-[#2b6cee] to-[#1a4fb8] rounded-3xl flex items-center justify-center mx-auto shadow-xl hover:scale-110 transition-transform duration-300 relative z-10">
                  <span className="text-4xl font-bold text-white">2</span>
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Candidates Interview</h3>
              <p className="text-gray-600 leading-relaxed">
                Candidates complete AI-powered interviews at their convenience. Our system 
                monitors and assesses them in real-time.
              </p>
            </div>

            {/* Step 3 */}
            <div className="text-center relative">
              <div className="relative mb-8 inline-block">
                <div className="w-24 h-24 bg-gradient-to-br from-[#2b6cee] to-[#1a4fb8] rounded-3xl flex items-center justify-center mx-auto shadow-xl hover:scale-110 transition-transform duration-300 relative z-10">
                  <span className="text-4xl font-bold text-white">3</span>
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Review & Hire</h3>
              <p className="text-gray-600 leading-relaxed">
                Review comprehensive reports, compare candidates, and make data-driven hiring 
                decisions with confidence.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Powerful Features for Modern Recruiters
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need to streamline your hiring process
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Feature 1 */}
            <div className="flex gap-6 bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition-all">
              <div className="flex-shrink-0">
                <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Icon name="smart_toy" size={28} className="text-blue-600" />
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">AI-Powered Interviews</h3>
                <p className="text-gray-600 leading-relaxed">
                  Our AI conducts natural, conversational interviews tailored to each role. 
                  Candidates feel comfortable while you get comprehensive assessments.
                </p>
              </div>
            </div>

            {/* Feature 2 */}
            <div className="flex gap-6 bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition-all">
              <div className="flex-shrink-0">
                <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center">
                  <Icon name="videocam" size={28} className="text-green-600" />
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Live Video Proctoring</h3>
                <p className="text-gray-600 leading-relaxed">
                  Real-time monitoring ensures interview integrity with facial recognition, 
                  tab switching detection, and behavior analysis.
                </p>
              </div>
            </div>

            {/* Feature 3 */}
            <div className="flex gap-6 bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition-all">
              <div className="flex-shrink-0">
                <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center">
                  <Icon name="assessment" size={28} className="text-purple-600" />
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Comprehensive Reports</h3>
                <p className="text-gray-600 leading-relaxed">
                  Detailed PDF reports with skill scores, behavioral insights, and hiring 
                  recommendations delivered instantly after each interview.
                </p>
              </div>
            </div>

            {/* Feature 4 */}
            <div className="flex gap-6 bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition-all">
              <div className="flex-shrink-0">
                <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center">
                  <Icon name="dashboard" size={28} className="text-orange-600" />
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Centralized Dashboard</h3>
                <p className="text-gray-600 leading-relaxed">
                  Manage all candidates, interviews, and reports from one intuitive dashboard. 
                  Track progress and collaborate with your team seamlessly.
                </p>
              </div>
            </div>

            {/* Feature 5 */}
            <div className="flex gap-6 bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition-all">
              <div className="flex-shrink-0">
                <div className="w-14 h-14 bg-pink-100 rounded-xl flex items-center justify-center">
                  <Icon name="auto_awesome" size={28} className="text-pink-600" />
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Smart Resume Matching</h3>
                <p className="text-gray-600 leading-relaxed">
                  AI-powered resume parsing and matching helps you identify the best candidates 
                  for each role based on skills and experience.
                </p>
              </div>
            </div>

            {/* Feature 6 */}
            <div className="flex gap-6 bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition-all">
              <div className="flex-shrink-0">
                <div className="w-14 h-14 bg-indigo-100 rounded-xl flex items-center justify-center">
                  <Icon name="integration_instructions" size={28} className="text-indigo-600" />
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Easy Integration</h3>
                <p className="text-gray-600 leading-relaxed">
                  Seamlessly integrate with your existing ATS and HR tools. Get started in 
                  minutes with our simple setup process.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

  
      {/* CTA Section - Ready to Transform */}
      <section id="pricing" className="py-20 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative">
            {/* Rounded Box with Gradient Background */}
            <div className="relative bg-gradient-to-br from-[#0f172a] via-[#1e3a8a] to-[#0f172a] rounded-3xl p-12 lg:p-16 overflow-visible">
              {/* Prismatic Background Effects */}
              <div className="absolute inset-0 overflow-hidden rounded-3xl">
                {/* Large center prismatic orb */}
                <div className="absolute left-1/4 top-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-gradient-to-br from-cyan-400/30 via-blue-500/35 to-indigo-600/30 blur-3xl"></div>
                
                {/* Top left - Purple to Pink spectrum */}
                <div className="absolute left-0 top-0 w-[400px] h-[400px] rounded-full bg-gradient-to-br from-purple-500/35 via-violet-500/30 to-fuchsia-500/25 blur-3xl"></div>
                
                {/* Bottom right - Blue to Cyan spectrum */}
                <div className="absolute right-0 bottom-0 w-[450px] h-[450px] rounded-full bg-gradient-to-tl from-blue-600/30 via-cyan-500/25 to-teal-400/20 blur-3xl"></div>
                
                {/* Accent orbs */}
                <div className="absolute left-1/3 bottom-1/4 w-[250px] h-[250px] rounded-full bg-gradient-to-r from-violet-400/20 to-fuchsia-400/15 blur-xl"></div>
              </div>

              <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                {/* Left Side - Content */}
                <div className="text-white lg:pr-8">
                  <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
                    Ready to Transform Your Hiring?
                  </h2>
                  <p className="text-lg sm:text-xl text-gray-200 mb-8">
                    Join hundreds of companies using Aptirix to hire better, faster, and smarter.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <Button 
                      size="xl" 
                      onClick={handleBookDemo}
                      className="bg-white text-gray-900 hover:bg-gray-100 px-8 py-4 text-lg font-semibold rounded-xl shadow-lg inline-flex items-center justify-center gap-2"
                    >
                      <Icon name="calendar_today" size={20} />
                      Book a Demo
                    </Button>
                    <Button 
                      size="xl" 
                      variant="outline"
                      onClick={handleContactUs}
                      className="border-2 border-white text-white hover:bg-white/10 px-8 py-4 text-lg font-semibold rounded-xl inline-flex items-center justify-center gap-2"
                    >
                      <Icon name="mail" size={20} />
                      Contact Us
                    </Button>
                  </div>
                </div>

                {/* Right Side - Image positioned to overflow */}
                <div className="relative lg:absolute lg:right-[-50px] lg:top-1/2 lg:-translate-y-1/2 lg:w-[50%] lg:max-w-[500px]">
                  <img 
                    src="https://citizenshighschool.com/wp-content/uploads/2024/03/Girl_on_laptop_transparernt-1024x1024.png"
                    alt="Professional with laptop"
                    className="w-full h-auto relative lg:scale-125 drop-shadow-2xl"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* Book Demo Modal */}
      <BookDemoModal
        isOpen={showBookDemoModal}
        onClose={() => setShowBookDemoModal(false)}
      />
    </div>
  );
};

export default RecruiterLandingPage;
